import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RefreshCw, Pencil, ExternalLink, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  adminTrustList,
  adminTrustUpdate,
  adminTrustSync,
  adminTrustSyncStatus,
  apiErr,
} from "@/lib/api";

const ALLOWED_TAGS = [
  "Güven", "Otorite", "Sosyal Kanıt", "Şeffaflık", "Sadakat",
  "Yakınlık", "Uzmanlık", "Kıtlık", "Kalite Algısı", "Mahalle Aidiyeti",
];

const FIELDS = [
  ["title", "Başlık", false],
  ["hook", "Hook", true],
  ["summary", "İçerik Özeti", true],
  ["purpose", "Amaç", true],
  ["emotion", "Verilen Duygu", true],
  ["trust_element", "Oluşturulan Güven Unsuru", true],
  ["psychological_trigger", "Psikolojik Tetikleyici", true],
  ["brand_contribution", "Marka Katkısı", true],
  ["loyalty_contribution", "Sadakat Katkısı", true],
  ["whatsapp_contribution", "WhatsApp Sipariş Katkısı", true],
  ["behavior_change", "Beklenen Davranış Değişikliği", true],
  ["target_outcome", "Hedeflenen Sonuç", true],
];

function EditDialog({ content, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => {
    const f = { tags: content.tags || [] };
    FIELDS.forEach(([k]) => (f[k] = content[k] || ""));
    return f;
  });
  const [saving, setSaving] = useState(false);

  const toggleTag = (t) =>
    setForm((p) => ({
      ...p,
      tags: p.tags.includes(t) ? p.tags.filter((x) => x !== t) : [...p.tags, t],
    }));

  const save = async () => {
    setSaving(true);
    try {
      await adminTrustUpdate(content.slug, form);
      toast.success("İçerik güncellendi");
      qc.invalidateQueries({ queryKey: ["admin-trust"] });
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trust-edit-dialog">
        <DialogHeader>
          <DialogTitle>İçerik Analizini Düzenle · #{content.idx}</DialogTitle>
          <DialogDescription className="sr-only">
            Bu içeriğin stratejik analizini ve psikoloji etiketlerini düzenleyin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <img src={content.media} alt="" className="w-full max-h-56 object-cover rounded-md" />
          {FIELDS.map(([k, label, multi]) => (
            <div key={k}>
              <label className="text-xs font-medium text-gray-600">{label}</label>
              {multi ? (
                <Textarea
                  data-testid={`trust-edit-${k}`}
                  rows={2}
                  value={form[k]}
                  onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                />
              ) : (
                <Input
                  data-testid={`trust-edit-${k}`}
                  value={form[k]}
                  onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
                />
              )}
            </div>
          ))}
          <div>
            <label className="text-xs font-medium text-gray-600">Psikoloji Etiketleri</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {ALLOWED_TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`trust-edit-tag-${t}`}
                  onClick={() => toggleTag(t)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                    form.tags.includes(t)
                      ? "border-[#C57B43] bg-[#C57B43] text-white"
                      : "border-gray-300 text-gray-600 hover:border-[#C57B43]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Vazgeç</Button>
          <Button onClick={save} disabled={saving} data-testid="trust-edit-save">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TrustManager() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const { data: contents = [], isLoading } = useQuery({
    queryKey: ["admin-trust"],
    queryFn: adminTrustList,
  });

  useEffect(() => () => clearInterval(pollRef.current), []);

  const startSync = async () => {
    setSyncing(true);
    try {
      await adminTrustSync();
      toast.info("Drive senkronizasyonu başlatıldı. Yeni içerikler analiz ediliyor…");
      pollRef.current = setInterval(async () => {
        try {
          const st = await adminTrustSyncStatus();
          if (st.status === "done") {
            clearInterval(pollRef.current);
            setSyncing(false);
            toast.success(`Senkronizasyon tamam. ${st.added || 0} yeni içerik eklendi.`);
            qc.invalidateQueries({ queryKey: ["admin-trust"] });
          } else if (st.status === "error") {
            clearInterval(pollRef.current);
            setSyncing(false);
            toast.error("Senkronizasyon hatası: " + (st.error || ""));
          }
        } catch {
          /* ignore */
        }
      }, 4000);
    } catch (e) {
      setSyncing(false);
      toast.error(apiErr(e));
    }
  };

  return (
    <div data-testid="trust-manager">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Marka Güven Merkezi</h3>
          <p className="text-sm text-gray-500">
            {contents.length} içerik · şifre korumalı sayfa:{" "}
            <a href="/marka-guven-merkezi" target="_blank" rel="noreferrer" className="text-[#C57B43] inline-flex items-center gap-1">
              /marka-guven-merkezi <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
        <Button onClick={startSync} disabled={syncing} data-testid="trust-sync-button" variant="outline">
          {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Drive'dan Yeniden Senkronize Et
        </Button>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contents.map((c) => (
            <div key={c.slug} className="rounded-lg border bg-white overflow-hidden" data-testid={`trust-card-${c.idx}`}>
              <div className="aspect-video bg-gray-100 overflow-hidden">
                <img src={c.media} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-medium text-sm leading-snug">{c.title}</h4>
                  {c.edited ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-green-700"><CheckCircle2 className="w-3 h-3" />Düzenlendi</span>
                  ) : (
                    <span className="shrink-0 text-[10px] text-amber-600">AI taslak</span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(c.tags || []).slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{t}</span>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setEditing(c)} data-testid={`trust-edit-button-${c.idx}`}>
                  <Pencil className="w-3.5 h-3.5 mr-2" />Düzenle
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <EditDialog content={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
